import * as C from 'fp-ts/es6/Const';
import { identity } from 'fp-ts/es6/function';
import * as I from 'fp-ts/es6/Identity';
import { pipe } from 'fp-ts/es6/pipeable';
import * as A from 'fp-ts/es6/ReadonlyArray';
import * as _ from './internal';
// -------------------------------------------------------------------------------------
// constructors
// -------------------------------------------------------------------------------------
/**
 * @category constructors
 * @since 2.3.0
 */
export var id = function () { return ({
    modifyF: function (_) { return function (f) { return f; }; }
}); };
/**
 * Create a `Traversal` from a `Traversable`
 *
 * @category constructor
 * @since 2.3.0
 */
export var fromTraversable = _.fromTraversable;
// -------------------------------------------------------------------------------------
// compositions
// -------------------------------------------------------------------------------------
/**
 * Compose a `Traversal` with a `Traversal`
 *
 * @category compositions
 * @since 2.3.0
 */
export var compose = _.traversalComposeTraversal;
// -------------------------------------------------------------------------------------
// combinators
// -------------------------------------------------------------------------------------
/**
 * @category combinators
 * @since 2.3.0
 */
export var modify = function (f) { return function (sa) {
    return sa.modifyF(I.identity)(f);
}; };
/**
 * @category combinators
 * @since 2.3.0
 */
export var set = function (a) {
    return modify(function () { return a; });
};
export function filter(predicate) {
    return compose(_.prismAsTraversal(_.prismFromPredicate(predicate)));
}
/**
 * Return a `Traversal` from a `Traversal` and a prop
 *
 * @category combinators
 * @since 2.3.0
 */
export var prop = function (prop) {
    return compose(pipe(_.lensId(), _.lensProp(prop), _.lensAsTraversal));
};
/**
 * Return a `Traversal` from a `Traversal` and a list of props
 *
 * @category combinators
 * @since 2.3.0
 */
export var props = function () {
    var props = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        props[_i] = arguments[_i];
    }
    return compose(pipe(_.lensId(), _.lensProps.apply(_, props), _.lensAsTraversal));
};
/**
 * Return a `Traversal` from a `Traversal` and a component
 *
 * @category combinators
 * @since 2.3.0
 */
export var component = function (prop) {
    return compose(pipe(_.lensId(), _.lensComponent(prop), _.lensAsTraversal));
};
/**
 * Return a `Traversal` from a `Traversal` focused on a `ReadonlyArray`
 *
 * @category combinators
 * @since 2.3.0
 */
export var index = function (i) { return function (sa) {
    return pipe(sa, compose(_.optionalAsTraversal(_.indexArray().index(i))));
}; };
/**
 * Return a `Traversal` from a `Traversal` focused on a `ReadonlyRecord` and a key
 *
 * @category combinators
 * @since 2.3.0
 */
export var key = function (key) { return function (sa) {
    return pipe(sa, compose(_.optionalAsTraversal(_.indexRecord().index(key))));
}; };
/**
 * Return a `Traversal` from a `Traversal` focused on a `ReadonlyRecord` and a required key
 *
 * @category combinators
 * @since 2.3.0
 */
export var atKey = function (key) { return function (sa) {
    return pipe(sa, compose(_.lensAsTraversal(_.atRecord().at(key))));
}; };
/**
 * Return a `Traversal` from a `Traversal` focused on the `Some` of a `Option` type
 *
 * @category combinators
 * @since 2.3.0
 */
export var some = 
/*#__PURE__*/
compose(_.prismAsTraversal(_.prismSome()));
/**
 * Return a `Traversal` from a `Traversal` focused on the `Right` of a `Either` type
 *
 * @category combinators
 * @since 2.3.0
 */
export var right = 
/*#__PURE__*/
compose(_.prismAsTraversal(_.prismRight()));
/**
 * Return a `Traversal` from a `Traversal` focused on the `Left` of a `Either` type
 *
 * @category combinators
 * @since 2.3.0
 */
export var left = 
/*#__PURE__*/
compose(_.prismAsTraversal(_.prismLeft()));
/**
 * Return a `Traversal` from a `Traversal` focused on a `Traversable`
 *
 * @category combinators
 * @since 2.3.0
 */
export function traverse(T) {
    return compose(fromTraversable(T)());
}
/**
 * Map each target to a `Monoid` and combine the results.
 *
 * @category combinators
 * @since 2.3.0
 */
export var foldMap = function (M) { return function (f) { return function (sa) {
    return sa.modifyF(C.getApplicative(M))(function (a) { return C.make(f(a)); });
}; }; };
/**
 * Map each target to a `Monoid` and combine the results.
 *
 * @category combinators
 * @since 2.3.0
 */
export var fold = function (M) { return foldMap(M)(identity); };
/**
 * Get all the targets of a `Traversal`.
 *
 * @category combinators
 * @since 2.3.0
 */
export var getAll = function (s) { return function (sa) {
    return foldMap(A.getMonoid())(function (a) { return [a]; })(sa)(s);
}; };
// -------------------------------------------------------------------------------------
// instances
// -------------------------------------------------------------------------------------
/**
 * @category instances
 * @since 2.3.0
 */
export var URI = 'monocle-ts/Traversal';
/**
 * @category instances
 * @since 2.3.0
 */
export var categoryTraversal = {
    URI: URI,
    compose: function (ab, ea) { return compose(ab)(ea); },
    id: id
};
